<template>
  <div class="watch-page">
    <header class="page-heading">
      <div>
        <p>Acquisitions</p>
        <h1>Veille</h1>
        <span>Vos recherches relancées automatiquement, avec alerte.</span>
      </div>
      <div class="heading-actions">
        <button
          type="button"
          class="refresh-button"
          :disabled="refreshing"
          :aria-label="refreshing ? 'Actualisation…' : 'Actualiser'"
          @click="refresh"
        >
          <span :class="{ spinning: refreshing }">↻</span
          ><span class="refresh-label">{{
            refreshing ? "Actualisation…" : "Actualiser"
          }}</span>
        </button>
        <button type="button" class="primary-action" @click="openEditor()">
          ＋ Nouvelle recherche
        </button>
      </div>
    </header>

    <section class="watch-panel">
      <nav class="tabs" aria-label="Veille">
        <button
          type="button"
          :class="{ active: tab === 'hits' }"
          @click="tab = 'hits'"
        >
          Nouveautés <b v-if="newCount">{{ newCount }}</b>
        </button>
        <button
          type="button"
          :class="{ active: tab === 'monitors' }"
          @click="tab = 'monitors'"
        >
          Recherches <small>{{ monitors.length }}</small>
        </button>
        <button
          type="button"
          :class="{ active: tab === 'settings' }"
          @click="tab = 'settings'"
        >
          Réglages
        </button>
      </nav>

      <!-- Nouveautés -->
      <section v-if="tab === 'hits'">
        <p v-if="unknownMonitor" class="notice" role="status">
          La recherche de ce lien n’existe pas (ou plus) dans cet espace :
          toutes les recherches sont affichées.
        </p>
        <div class="toolbar">
          <select
            v-model="hitFilter.monitor"
            aria-label="Filtrer par recherche"
          >
            <option value="">Toutes les recherches</option>
            <option
              v-for="monitor in monitors"
              :key="monitor.id"
              :value="monitor.id"
            >
              {{ monitor.name }}
            </option>
          </select>
          <select v-model="hitFilter.status" aria-label="Filtrer par statut">
            <option value="new">Nouveautés</option>
            <option value="ignored">Ignorées</option>
            <option value="baseline">Déjà en ligne au 1er passage</option>
            <option value="all">Toutes</option>
          </select>
          <button
            v-if="hitFilter.status === 'new' && hits.length"
            type="button"
            class="reset"
            @click="ignoreAll"
          >
            Tout ignorer
          </button>
        </div>
        <p v-if="loadingHits" class="empty">Chargement…</p>
        <p v-else-if="!hits.length" class="empty">
          <template v-if="!monitors.length">
            Créez votre première recherche pour être prévenu des nouvelles
            annonces.
          </template>
          <template v-else-if="hitFilter.status === 'new'">
            Aucune nouveauté pour l’instant. Au premier passage d’une recherche,
            les annonces déjà en ligne sont mémorisées sans alerte : seules les
            suivantes apparaissent ici.
            <br />
            <button
              type="button"
              class="link"
              @click="hitFilter.status = 'baseline'"
            >
              Voir les annonces déjà en ligne
            </button>
          </template>
          <template v-else>Aucune annonce pour ce filtre.</template>
        </p>
        <div v-else class="hits">
          <article
            v-for="hit in hits"
            :key="hit.id"
            class="hit"
            :class="`hit--${hit.status}`"
          >
            <!-- The image enlarged here, to sort the results without opening
                 the site (the links stay on the right) -->
            <button
              v-if="hit.image && !hit.__imageFailed"
              type="button"
              class="hit__image hit__image--zoom"
              title="Agrandir l’image"
              @click="zoomed = hit"
            >
              <img
                :src="hitImage(hit)"
                :alt="hit.title"
                loading="lazy"
                @error="hit.__imageFailed = true"
              />
            </button>
            <a
              v-else
              :href="hit.links?.view?.url || hit.url"
              target="_blank"
              rel="noopener"
              class="hit__image"
              ><span>◎</span></a
            >
            <div class="hit__body">
              <span class="badge">{{ connectorName(hit.connector) }}</span>
              <strong>{{ hit.title }}</strong>
              <small
                v-if="hit.title_original && hit.title_original !== hit.title"
                class="original"
                >{{ hit.title_original }}</small
              >
              <span v-if="hit.previous_price" class="drop">
                📉 {{ formatPrice(hit.previous_price, hit.currency) }} →
                {{ formatPrice(hit.price, hit.currency) }} (−{{
                  hit.price_drop_pct
                }}
                %)
              </span>
              <span class="price">
                {{ formatPrice(hit.price, hit.currency)
                }}<template v-if="hit.price_eur && hit.currency !== 'EUR'">
                  · ≈ {{ formatPrice(hit.price_eur, "EUR") }}</template
                >
              </span>
              <CoteChip
                v-if="hit.cote"
                :cote="hit.cote"
                :price="hit.currency === 'JPY' ? hit.price : null"
              />
              <small
                v-else-if="hit.status === 'followed'"
                class="meta cote-state"
                >{{ coteState(hit.cote_status) }}</small
              >
              <small class="meta">
                <button
                  v-if="hit.monitor_id && hitFilter.monitor !== hit.monitor_id"
                  type="button"
                  class="meta-link"
                  :title="`Voir les résultats de « ${hit.monitor_name} »`"
                  @click="hitFilter.monitor = hit.monitor_id"
                >
                  {{ hit.monitor_name }}</button
                ><template v-else>{{ hit.monitor_name }}</template> ·
                {{ relative(hit.found_at)
                }}<template v-if="hit.ends_at">
                  · fin {{ formatDate(hit.ends_at) }}</template
                >
              </small>
              <small v-if="hit.listed_at" class="meta">
                Publiée le {{ formatDate(hit.listed_at)
                }}<template
                  v-if="
                    hit.updated_at &&
                    hit.updated_at.slice(0, 16) !== hit.listed_at.slice(0, 16)
                  "
                >
                  · mise à jour le {{ formatDate(hit.updated_at) }}</template
                >
              </small>
            </div>
            <div class="hit__actions">
              <template
                v-if="hit.status === 'new' || hit.status === 'baseline'"
              >
                <button
                  type="button"
                  class="follow-button"
                  :disabled="hit.__busy"
                  @click="followHit(hit)"
                >
                  Suivre
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  :disabled="hit.__busy"
                  @click="ignoreHit(hit)"
                >
                  Ignorer
                </button>
              </template>
              <template v-else>
                <!-- Followed: from now on in « Annonces suivies » only (the card
                     leaves the watch at the next refresh) -->
                <router-link
                  v-if="hit.status === 'followed' && hit.offer_id"
                  :to="`/offers?open=offer:${encodeURIComponent(hit.offer_id)}`"
                  class="status status--link"
                  title="Voir l’annonce dans Annonces suivies"
                  >✓ Suivie · voir dans Annonces suivies →</router-link
                >
                <span v-else class="status">{{
                  { followed: "✓ Suivie", ignored: "Ignorée" }[hit.status]
                }}</span>
                <button
                  type="button"
                  class="undo-button"
                  :disabled="hit.__busy"
                  @click="restoreHit(hit)"
                >
                  {{
                    hit.status === "followed" ? "Ne plus suivre" : "Remettre"
                  }}
                </button>
              </template>
              <ListingLinks
                v-if="hit.links"
                :links="hit.links"
                compact
                class="hit__links"
              />
              <a
                v-else
                :href="hit.url"
                target="_blank"
                rel="noopener"
                class="link"
                >Ouvrir ↗</a
              >
            </div>
          </article>
        </div>
      </section>

      <!-- Recherches -->
      <section v-else-if="tab === 'monitors'" class="monitors">
        <p v-if="!monitors.length" class="empty">
          Aucune recherche. « Nouvelle recherche » pour commencer.
        </p>
        <article
          v-for="monitor in monitors"
          :key="monitor.id"
          class="monitor"
          :class="{ paused: !monitor.active }"
        >
          <div class="monitor__main">
            <div class="monitor__title">
              <span class="badge">{{ connectorName(monitor.connector) }}</span>
              <strong>{{ monitor.name }}</strong>
              <b
                v-if="monitor.new_count"
                class="count"
                @click="showHitsFor(monitor)"
                >{{ monitor.new_count }} nouveauté{{
                  monitor.new_count > 1 ? "s" : ""
                }}</b
              >
            </div>
            <small>{{ criteriaSummary(monitor) }}</small>
            <small>
              {{ frequencyLabel(monitor.frequency_minutes) }} ·
              <template v-if="monitor.notify_price_drops"
                >📉 baisses de prix ·</template
              >
              {{ transportLabel(monitor.transport) }} ·
              <template v-if="!monitor.active">en pause</template>
              <template v-else-if="!monitor.last_run_at"
                >premier passage en attente</template
              >
              <template v-else-if="monitor.last_status === 'error'"
                ><span class="error">⚠ {{ monitor.last_error }}</span></template
              >
              <template v-else
                >dernier passage {{ relative(monitor.last_run_at) }} ({{
                  monitor.last_result_count ?? 0
                }}
                résultats)<span
                  v-if="monitor.last_truncated"
                  class="warning"
                  title="Plus de 3 pages de nouveautés depuis le passage précédent : passez la veille à une fréquence plus courte ou précisez la recherche"
                >
                  · ⚠ beaucoup de nouveautés, certaines ont pu être
                  manquées</span
                ></template
              >
            </small>
          </div>
          <div class="monitor__actions">
            <label class="switch"
              ><input
                type="checkbox"
                :checked="monitor.active"
                @change="toggleActive(monitor)"
              />
              Active</label
            >
            <button
              type="button"
              class="link"
              :disabled="monitor.__running"
              @click="runNow(monitor)"
            >
              {{ monitor.__running ? "Lancée…" : "Lancer maintenant" }}
            </button>
            <button type="button" class="link" @click="showResultsFor(monitor)">
              Voir les résultats
            </button>
            <button type="button" class="link" @click="openEditor(monitor)">
              Modifier
            </button>
            <button
              type="button"
              class="link link--danger"
              @click="removeMonitor(monitor)"
            >
              Supprimer
            </button>
          </div>
        </article>
      </section>

      <!-- Réglages -->
      <section v-else class="settings">
        <h2>Mon proxy</h2>
        <p class="hint">
          Certains sites refusent les connexions venant d’un serveur (Vinted) ou
          d’Europe (Yahoo! Japon). Renseignez ici un proxy à vous, par exemple
          installé sur un ordinateur de votre domicile, puis choisissez le
          transport « Mon proxy » dans la recherche concernée.
        </p>
        <label class="field">
          Adresse du proxy
          <input
            v-model.trim="proxyUrl"
            placeholder="http://utilisateur:motdepasse@adresse:3128"
            autocomplete="off"
          />
        </label>
        <p v-if="proxyMasked" class="hint">Enregistré : {{ proxyMasked }}</p>
        <div class="buttons">
          <button
            type="button"
            class="primary"
            :disabled="savingProxy"
            @click="saveProxy"
          >
            Enregistrer
          </button>
          <button
            v-if="proxyMasked"
            type="button"
            class="link link--danger"
            @click="
              proxyUrl = '';
              saveProxy();
            "
          >
            Retirer
          </button>
        </div>
        <p v-if="proxyMessage" class="hint" :class="{ error: proxyError }">
          {{ proxyMessage }}
        </p>
      </section>
    </section>

    <!-- Éditeur de recherche -->
    <div
      v-if="editor.open"
      class="drawer-layer"
      @mousedown="drawerDown"
      @click="drawerClick"
    >
      <aside class="drawer">
        <header>
          <h2>
            {{ editor.id ? "Modifier la recherche" : "Nouvelle recherche" }}
          </h2>
          <button type="button" class="close" @click="closeEditor">×</button>
        </header>
        <form @submit.prevent="saveMonitor">
          <div v-if="!editor.id" class="import">
            <label class="field">
              Importer depuis une URL (facultatif)
              <input
                v-model.trim="editor.importUrl"
                type="url"
                placeholder="Collez l’adresse d’une page de résultats Yahoo!, Mercari ou Vinted"
                @keydown.enter.prevent="importUrl"
              />
            </label>
            <button
              type="button"
              class="secondary small"
              :disabled="!editor.importUrl || editor.importing"
              @click="importUrl"
            >
              {{ editor.importing ? "Analyse…" : "Importer" }}
            </button>
            <p class="hint">
              Faites votre recherche sur le site avec tous ses filtres, copiez
              l’adresse de la page de résultats et collez-la ici : le formulaire
              est pré-rempli.
            </p>
            <p
              v-if="editor.importMessage"
              class="hint"
              :class="{ error: editor.importError }"
            >
              {{ editor.importMessage }}
            </p>
          </div>
          <label class="field">
            Source
            <select
              v-model="editor.connector"
              :disabled="Boolean(editor.id)"
              @change="onConnectorChange"
            >
              <option
                v-for="connector in connectorsList"
                :key="connector.id"
                :value="connector.id"
              >
                {{ connector.name }}
              </option>
            </select>
          </label>
          <p v-if="currentConnector?.transport_note" class="hint">
            {{ currentConnector.transport_note }}
          </p>

          <template
            v-for="filter in currentConnector?.filters || []"
            :key="filter.name"
          >
            <label v-if="filter.type === 'boolean'" class="switch">
              <input v-model="editor.criteria[filter.name]" type="checkbox" />
              {{ filter.label }}
            </label>
            <label v-else class="field">
              {{ filter.label
              }}<template v-if="filter.unit"> ({{ filter.unit }})</template>
              <select
                v-if="filter.type === 'select'"
                v-model="editor.criteria[filter.name]"
              >
                <option
                  v-for="option in filter.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <input
                v-else
                v-model="editor.criteria[filter.name]"
                :type="filter.type === 'number' ? 'number' : 'text'"
                :placeholder="filter.placeholder || ''"
                min="0"
              />
            </label>
          </template>

          <div v-if="advancedCount" class="advanced">
            <p>
              Paramètres avancés importés de l’URL, transmis tels quels au site
              :
              <button type="button" class="link" @click="clearAdvanced">
                Tout retirer
              </button>
            </p>
            <p v-if="describing" class="hint">Recherche des libellés…</p>
            <div
              v-for="entry in paramDescriptions"
              :key="entry.name"
              class="param"
            >
              <span class="param__label">{{ entry.label }}</span>
              <span v-for="value in entry.values" :key="value.id" class="chip">
                {{ value.label }}
                <button
                  type="button"
                  :aria-label="`Retirer ${value.label}`"
                  @click="removeParamValue(entry.name, value.id)"
                >
                  ×
                </button>
              </span>
            </div>
          </div>
          <label class="field">
            Nom de la recherche
            <input
              v-model.trim="editor.name"
              :placeholder="editor.criteria.keywords || 'ex. CoreGrafx Mercari'"
            />
          </label>
          <label class="field">
            Fréquence
            <select v-model.number="editor.frequency_minutes">
              <option
                v-for="option in frequencies"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="field">
            Transport
            <select v-model="editor.transport">
              <option
                v-for="option in transports"
                :key="option.id"
                :value="option.id"
                :disabled="!option.available"
              >
                {{ option.label
                }}{{ option.available ? "" : " (non configuré)" }}
              </option>
            </select>
          </label>
          <label class="switch"
            ><input v-model="editor.active" type="checkbox" /> Recherche
            active</label
          >
          <label class="switch"
            ><input v-model="editor.notify_price_drops" type="checkbox" /> Me
            prévenir aussi des baisses de prix (annonces déjà vues)</label
          >
          <p class="hint">
            Au premier passage, les annonces déjà en ligne sont mémorisées sans
            alerte : seules les suivantes vous sont signalées.
          </p>
          <p v-if="editor.error" class="error">{{ editor.error }}</p>
          <footer>
            <button type="button" class="secondary" @click="closeEditor">
              Annuler
            </button>
            <button type="submit" class="primary" :disabled="editor.saving">
              {{ editor.saving ? "Enregistrement…" : "Enregistrer" }}
            </button>
          </footer>
        </form>
      </aside>
    </div>
    <Teleport to="body">
      <div
        v-if="zoomed"
        class="hit-zoom"
        role="dialog"
        aria-modal="true"
        :aria-label="zoomed.title"
        @click.self="zoomed = null"
      >
        <button
          type="button"
          class="hit-zoom__close"
          aria-label="Fermer"
          @click="zoomed = null"
        >
          ×
        </button>
        <img
          :key="zoomed.id"
          :src="zoomSource(zoomed)"
          :alt="zoomed.title"
          @error="zoomed.__largeFailed = true"
          @click="zoomed = null"
        />
        <div class="hit-zoom__bar">
          <div class="hit-zoom__text">
            <strong>{{ zoomed.title }}</strong>
            <span
              >{{ formatPrice(zoomed.price, zoomed.currency)
              }}<template v-if="zoomed.price_eur && zoomed.currency !== 'EUR'">
                · ≈ {{ formatPrice(zoomed.price_eur, "EUR") }}</template
              ></span
            >
          </div>
          <div class="hit-zoom__actions">
            <template
              v-if="zoomed.status === 'new' || zoomed.status === 'baseline'"
            >
              <button
                type="button"
                class="follow-button"
                :disabled="zoomed.__busy"
                @click="zoomAction(followHit)"
              >
                Suivre
              </button>
              <button
                type="button"
                class="ghost-button"
                :disabled="zoomed.__busy"
                @click="zoomAction(ignoreHit)"
              >
                Ignorer
              </button>
            </template>
            <ListingLinks
              v-if="zoomed.links"
              :links="zoomed.links"
              compact
              class="hit__links"
            />
            <a
              v-else
              :href="zoomed.url"
              target="_blank"
              rel="noopener"
              class="link"
              >Ouvrir ↗</a
            >
          </div>
        </div>
      </div>
    </Teleport>
    <div v-if="undo" class="undo-toast" role="status">
      <span>Annonce ignorée.</span>
      <button type="button" @click="undoIgnore">Annuler</button>
    </div>
  </div>
</template>

<script>
import { formatDate as formatDateText, parseTime } from "../../clock";
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from "vue";
import { dropQueryParams } from "../../urlQuery";
import ListingLinks from "../common/ListingLinks.vue";
import CoteChip from "../common/CoteChip.vue";
import { useRoute } from "vue-router";
import axios from "axios";
import config from "@/config/config.js";

const FREQUENCIES = [
  { value: 5, label: "Toutes les 5 min" },
  { value: 10, label: "Toutes les 10 min" },
  { value: 15, label: "Toutes les 15 min" },
  { value: 30, label: "Toutes les 30 min" },
  { value: 60, label: "Toutes les heures" },
  { value: 120, label: "Toutes les 2 h" },
  { value: 360, label: "Toutes les 6 h" },
  { value: 720, label: "Toutes les 12 h" },
  { value: 1440, label: "Une fois par jour" },
];

export default {
  name: "WatchPage",
  components: { CoteChip, ListingLinks },
  setup() {
    const route = useRoute();
    const tab = ref("hits");
    const connectorsList = ref([]);
    const transports = ref([]);
    const monitors = ref([]);
    const hits = ref([]);
    const loadingHits = ref(false);
    const hitFilter = reactive({
      monitor: String(route.query.monitor || ""),
      status: "new",
    });
    const proxyUrl = ref("");
    const proxyMasked = ref("");
    const proxyMessage = ref("");
    const proxyError = ref(false);
    const savingProxy = ref(false);
    const editor = reactive({
      open: false,
      id: null,
      connector: "",
      name: "",
      criteria: {},
      frequency_minutes: 15,
      transport: "direct",
      active: true,
      notify_price_drops: false,
      saving: false,
      error: "",
      importUrl: "",
      importing: false,
      importMessage: "",
      importError: false,
    });

    const currentConnector = computed(() =>
      connectorsList.value.find(
        (connector) => connector.id === editor.connector,
      ),
    );
    const newCount = computed(() =>
      monitors.value.reduce(
        (sum, monitor) => sum + (monitor.new_count || 0),
        0,
      ),
    );
    const connectorName = (id) =>
      connectorsList.value.find((connector) => connector.id === id)?.name || id;
    const transportLabel = (id) =>
      transports.value.find((option) => option.id === id)?.label || id;
    const frequencyLabel = (minutes) =>
      FREQUENCIES.find((option) => option.value === minutes)?.label ||
      `Toutes les ${minutes} min`;
    const formatPrice = (value, currency) =>
      value == null
        ? "Prix non indiqué"
        : new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: currency || "JPY",
            maximumFractionDigits: currency === "JPY" ? 0 : 2,
          }).format(value);
    const formatDate = (value) =>
      formatDateText(value, { dateStyle: "short", timeStyle: "short" });
    const relative = (value) => {
      const minutes = Math.round((Date.now() - parseTime(value)) / 60000);
      if (Number.isNaN(minutes)) return "";
      if (minutes < 1) return "à l’instant";
      if (minutes < 60) return `il y a ${minutes} min`;
      if (minutes < 1440) return `il y a ${Math.round(minutes / 60)} h`;
      return formatDate(value);
    };
    const criteriaSummary = (monitor) => {
      const connector = connectorsList.value.find(
        (entry) => entry.id === monitor.connector,
      );
      return (connector?.filters || [])
        .filter(
          (filter) =>
            monitor.criteria?.[filter.name] !== undefined &&
            monitor.criteria[filter.name] !== "" &&
            monitor.criteria[filter.name] !== false,
        )
        .map((filter) => {
          const value = monitor.criteria[filter.name];
          if (filter.type === "select")
            return (
              filter.options.find((option) => option.value === String(value))
                ?.label || value
            );
          if (filter.type === "boolean") return filter.label;
          return `${filter.label} : ${value}${filter.unit ? ` ${filter.unit}` : ""}`;
        })
        .concat(
          monitor.params_display?.length
            ? monitor.params_display
            : Object.keys(monitor.criteria?.params || {}).length
              ? [
                  `+ ${Object.keys(monitor.criteria.params).length} paramètre(s) avancé(s)`,
                ]
              : [],
        )
        .join(" · ");
    };

    // Link of a notification for a search unknown here (deleted, or sent by
    // the other environment): show every search instead of an empty list
    const unknownMonitor = ref(false);
    const loadMonitors = async () => {
      const { data } = await axios.get(`${config.apiUrl}/monitors`);
      monitors.value = (data || []).map((monitor) => ({
        ...monitor,
        __running: false,
      }));
      if (
        hitFilter.monitor &&
        !monitors.value.some((monitor) => monitor.id === hitFilter.monitor)
      ) {
        unknownMonitor.value = true;
        hitFilter.monitor = "";
      }
    };
    // silent: refresh without the "Chargement…" placeholder
    // Only the answer to the latest request counts (filter changed during an
    // automatic refresh): never the results of the previous filter
    let hitsRequest = 0;
    const loadHits = async ({ silent = false } = {}) => {
      const request = ++hitsRequest;
      if (!silent) loadingHits.value = true;
      try {
        const { data } = await axios.get(`${config.apiUrl}/monitor-hits`, {
          params: {
            status: hitFilter.status,
            monitor: hitFilter.monitor || undefined,
            limit: 120,
          },
        });
        if (request !== hitsRequest) return;
        const busy = new Set(
          hits.value.filter((hit) => hit.__busy).map((hit) => hit.id),
        );
        hits.value = (data || []).map((hit) => ({
          ...hit,
          __busy: busy.has(hit.id),
        }));
      } finally {
        if (request === hitsRequest) loadingHits.value = false;
      }
    };
    const loadTransport = async () => {
      const { data } = await axios.get(`${config.apiUrl}/monitors/transport`);
      proxyMasked.value = data?.proxy_url || "";
    };
    const loadConnectors = async () => {
      const { data } = await axios.get(`${config.apiUrl}/connectors`);
      connectorsList.value = data?.connectors || [];
      transports.value = data?.transports || [];
    };
    const reload = () => Promise.all([loadMonitors(), loadHits()]);
    watch(hitFilter, () => loadHits());

    // Refresh: button, back to the browser tab, every minute while the page
    // is shown, and on return to the page if it is ever kept alive
    const refreshing = ref(false);
    const refresh = async () => {
      if (refreshing.value) return;
      refreshing.value = true;
      try {
        await Promise.all([loadMonitors(), loadHits({ silent: true })]);
      } catch {
        // network hiccup: the next refresh will try again
      } finally {
        refreshing.value = false;
      }
    };
    const AUTO_REFRESH_MS = 60 * 1000;
    let active = false;
    let timer = null;
    const startAutoRefresh = () => {
      clearInterval(timer);
      timer = setInterval(() => {
        if (active && document.visibilityState === "visible") refresh();
      }, AUTO_REFRESH_MS);
    };
    const onVisible = () => {
      if (active && document.visibilityState === "visible") refresh();
    };
    let firstActivation = true;
    onActivated(() => {
      active = true;
      startAutoRefresh();
      if (firstActivation) firstActivation = false;
      else refresh();
    });
    onDeactivated(() => {
      active = false;
      clearInterval(timer);
    });
    document.addEventListener("visibilitychange", onVisible);
    // A guide computed in a sheet: the followed items of this offer show it
    const onChanged = (event) => {
      const { type, record } = event.detail || {};
      if (type !== "offer" || !record?._key || !("cote" in record)) return;
      hits.value
        .filter((hit) => hit.offer_id === record._key)
        .forEach((hit) => (hit.cote = record.cote));
    };
    window.addEventListener("prexens:changed", onChanged);
    onUnmounted(() => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("prexens:changed", onChanged);
    });

    // A followed item without guide: why (see the "Cote" block of its sheet)
    const COTE_STATES = {
      pending: "Cote : calcul en cours…",
      no_ai: "Cote : IA non configurée sur le serveur",
      unidentified: "Cote : produit non reconnu (à indiquer dans la fiche)",
      error: "Cote : le calcul a échoué (détail dans la fiche)",
      ready: "Cote : aucune vente terminée trouvée",
    };
    const coteState = (status) =>
      COTE_STATES[status] || "Cote : pas encore calculée (voir la fiche)";

    // ---------- Nouveautés ----------
    // Image enlarged: the site's big version when there is one (Mercari),
    // else the image of the list (Yahoo: stored by Prexens)
    const zoomed = ref(null);
    const zoomSource = (hit) =>
      hit.image_large && !hit.__largeFailed ? hit.image_large : hitImage(hit);
    const zoomAction = async (action) => {
      const hit = zoomed.value;
      await action(hit);
      zoomed.value = null;
    };
    const closeZoom = (event) => {
      if (event.key === "Escape" && zoomed.value) {
        event.stopPropagation();
        zoomed.value = null;
      }
    };
    document.addEventListener("keydown", closeZoom, true);
    onUnmounted(() => document.removeEventListener("keydown", closeZoom, true));
    const followHit = async (hit) => {
      hit.__busy = true;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/monitor-hits/${hit.id}/follow`,
        );
        // Its offer: the link "voir dans Annonces suivies" right away
        hit.offer_id = data?.offer_id || hit.offer_id;
        hit.status = "followed";
        loadMonitors();
      } catch {
        window.alert("L’annonce n’a pas pu être suivie.");
      } finally {
        hit.__busy = false;
      }
    };
    // A click by mistake can be undone: "Annuler" just after "Ignorer", and
    // "Ne plus suivre" / "Remettre" on the followed or ignored items
    const undo = ref(null);
    let undoTimer = null;
    const ignoreHit = async (hit) => {
      hit.__busy = true;
      try {
        await axios.post(`${config.apiUrl}/monitor-hits/${hit.id}/ignore`);
        const index = hits.value.findIndex((entry) => entry.id === hit.id);
        hits.value = hits.value.filter(
          (entry) => entry.id !== hit.id || hitFilter.status !== "new",
        );
        hit.status = "ignored";
        undo.value = { hit, index };
        clearTimeout(undoTimer);
        undoTimer = setTimeout(() => (undo.value = null), 8000);
        loadMonitors();
      } finally {
        hit.__busy = false;
      }
    };
    const restoreHit = async (hit) => {
      if (
        hit.status === "followed" &&
        !window.confirm(
          "Ne plus suivre cette annonce ? Ses alertes de fin d’enchère sont annulées et elle revient dans les nouveautés.",
        )
      )
        return false;
      hit.__busy = true;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/monitor-hits/${hit.id}/restore`,
        );
        hit.status = data.status;
        hit.offer_id = null;
        loadMonitors();
        return true;
      } catch {
        window.alert("L’annonce n’a pas pu être remise.");
        return false;
      } finally {
        hit.__busy = false;
      }
    };
    const undoIgnore = async () => {
      const { hit, index } = undo.value || {};
      undo.value = null;
      clearTimeout(undoTimer);
      if (!hit || !(await restoreHit(hit))) return;
      if (!hits.value.some((entry) => entry.id === hit.id))
        hits.value.splice(Math.max(index, 0), 0, hit);
    };
    const ignoreAll = async () => {
      if (!window.confirm("Ignorer toutes les nouveautés affichées ?")) return;
      // Only the new items shown (not those found since)
      await axios.post(`${config.apiUrl}/monitor-hits/ignore-all`, {
        monitor: hitFilter.monitor || null,
        ids: hits.value
          .filter((hit) => hit.status === "new")
          .map((hit) => hit.id),
      });
      reload();
    };
    watch(
      () => hitFilter.monitor,
      (value) => {
        if (value) unknownMonitor.value = false;
      },
    );
    // Images blocked outside the site's country (Yahoo!): served by Prexens
    const hitImage = (hit) =>
      hit.image_proxied
        ? `${config.apiUrl}/monitor-hits/${hit.id}/image`
        : hit.image;
    const showHitsFor = (monitor) => {
      hitFilter.monitor = monitor.id;
      hitFilter.status = "new";
      tab.value = "hits";
    };
    // Tous les résultats connus de la recherche, y compris ceux du 1er passage
    const showResultsFor = (monitor) => {
      hitFilter.monitor = monitor.id;
      hitFilter.status = "all";
      tab.value = "hits";
    };

    // ---------- Recherches ----------
    const defaultsFor = (connector) =>
      Object.fromEntries(
        (connector?.filters || []).map((filter) => [
          filter.name,
          filter.default ?? (filter.type === "boolean" ? false : ""),
        ]),
      );
    const openEditor = (monitor = null) => {
      const connector = connectorsList.value.find(
        (entry) =>
          entry.id === (monitor?.connector || connectorsList.value[0]?.id),
      );
      Object.assign(editor, {
        open: true,
        id: monitor?.id || null,
        connector: connector?.id || "",
        name: monitor?.name || "",
        criteria: { ...defaultsFor(connector), ...(monitor?.criteria || {}) },
        frequency_minutes: monitor?.frequency_minutes || 15,
        transport:
          monitor?.transport || connector?.default_transport || "direct",
        active: monitor ? monitor.active : true,
        notify_price_drops: monitor
          ? Boolean(monitor.notify_price_drops)
          : false,
        saving: false,
        error: "",
        importUrl: "",
        importing: false,
        importMessage: "",
        importError: false,
      });
      paramDescriptions.value = [];
      if (monitor?.criteria?.params) describeParams();
      editorOpened = editorSnapshot();
    };
    // The search as opened: closing after a change asks first
    let editorOpened = "";
    const editorSnapshot = () =>
      JSON.stringify([
        editor.connector,
        editor.name,
        editor.criteria,
        editor.frequency_minutes,
        editor.transport,
        editor.active,
        editor.notify_price_drops,
      ]);
    const closeEditor = () => {
      if (
        !editor.saving &&
        editorSnapshot() !== editorOpened &&
        !window.confirm("Fermer sans enregistrer la recherche ?")
      )
        return;
      editor.open = false;
    };
    // A click on the background closes, not the end of a text selection
    let pressedOnBackground = false;
    const drawerDown = (event) => {
      pressedOnBackground = event.target === event.currentTarget;
    };
    const drawerClick = (event) => {
      if (pressedOnBackground && event.target === event.currentTarget)
        closeEditor();
      pressedOnBackground = false;
    };
    const advancedCount = computed(
      () => Object.keys(editor.criteria?.params || {}).length,
    );
    const clearAdvanced = () => {
      const criteria = { ...editor.criteria };
      delete criteria.params;
      editor.criteria = criteria;
      paramDescriptions.value = [];
    };
    // Libellés lisibles des paramètres avancés (noms de catégories, marques…)
    const paramDescriptions = ref([]);
    const describing = ref(false);
    const describeParams = async () => {
      const params = editor.criteria?.params || {};
      if (!Object.keys(params).length || !editor.connector) {
        paramDescriptions.value = [];
        return;
      }
      // Affichage immédiat avec les identifiants, puis les libellés
      paramDescriptions.value = Object.entries(params).map(([name, value]) => ({
        name,
        label: name,
        values: (Array.isArray(value) ? value : [value]).map((id) => ({
          id: String(id),
          label: String(id),
        })),
      }));
      describing.value = true;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/connectors/${editor.connector}/describe-params`,
          { params },
        );
        paramDescriptions.value = data || paramDescriptions.value;
      } catch {
        /* les identifiants restent affichés */
      } finally {
        describing.value = false;
      }
    };
    const removeParamValue = (name, id) => {
      const params = { ...(editor.criteria.params || {}) };
      const current = params[name];
      if (Array.isArray(current) && current.length > 1)
        params[name] = current.filter((value) => String(value) !== id);
      else delete params[name];
      const criteria = { ...editor.criteria, params };
      if (!Object.keys(params).length) delete criteria.params;
      editor.criteria = criteria;
      paramDescriptions.value = paramDescriptions.value
        .map((entry) =>
          entry.name === name
            ? {
                ...entry,
                values: entry.values.filter((value) => value.id !== id),
              }
            : entry,
        )
        .filter((entry) => entry.values.length);
    };
    // URL d'une page de résultats → source + formulaire pré-rempli
    const importUrl = async () => {
      if (!editor.importUrl) return;
      editor.importing = true;
      editor.importMessage = "";
      editor.importError = false;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/connectors/parse-url`,
          { url: editor.importUrl },
        );
        const connector = connectorsList.value.find(
          (entry) => entry.id === data.connector,
        );
        editor.connector = data.connector;
        editor.criteria = { ...defaultsFor(connector), ...data.criteria };
        describeParams();
        const transport = transports.value.find(
          (option) => option.id === data.transport,
        );
        editor.transport =
          transport?.available || !transports.value.length
            ? data.transport
            : "direct";
        if (!editor.name && data.criteria.keywords)
          editor.name = `${data.criteria.keywords} · ${connector?.name || data.connector}`;
        editor.importMessage = `Recherche ${connector?.name || data.connector} importée.${
          data.ignored?.length
            ? ` Paramètre${data.ignored.length > 1 ? "s" : ""} non pris en charge, ignoré${data.ignored.length > 1 ? "s" : ""} : ${data.ignored.join(", ")}.`
            : ""
        }`;
      } catch (error) {
        editor.importError = true;
        editor.importMessage =
          error.response?.data?.error ||
          "Cette adresse n’a pas pu être analysée.";
      } finally {
        editor.importing = false;
      }
    };
    const onConnectorChange = () => {
      const connector = currentConnector.value;
      editor.criteria = {
        ...defaultsFor(connector),
        keywords: editor.criteria.keywords || "",
      };
      editor.transport = connector?.default_transport || "direct";
    };
    const saveMonitor = async () => {
      editor.saving = true;
      editor.error = "";
      const payload = {
        connector: editor.connector,
        name: editor.name,
        criteria: editor.criteria,
        frequency_minutes: editor.frequency_minutes,
        transport: editor.transport,
        active: editor.active,
        notify_price_drops: editor.notify_price_drops,
      };
      try {
        if (editor.id)
          await axios.put(`${config.apiUrl}/monitors/${editor.id}`, payload);
        else await axios.post(`${config.apiUrl}/monitors`, payload);
        editor.open = false;
        tab.value = "monitors";
        await loadMonitors();
      } catch (error) {
        editor.error =
          error.response?.data?.error ||
          "La recherche n’a pas pu être enregistrée.";
      } finally {
        editor.saving = false;
      }
    };
    const toggleActive = async (monitor) => {
      await axios.put(`${config.apiUrl}/monitors/${monitor.id}`, {
        active: !monitor.active,
      });
      await loadMonitors();
    };
    const runNow = async (monitor) => {
      monitor.__running = true;
      await axios.post(`${config.apiUrl}/monitors/${monitor.id}/run`);
      // Le worker exécute la recherche : on rafraîchit après quelques secondes
      setTimeout(async () => {
        await reload();
      }, 15000);
    };
    const removeMonitor = async (monitor) => {
      if (
        !window.confirm(
          `Supprimer la recherche « ${monitor.name} » et ses nouveautés ?`,
        )
      )
        return;
      await axios.delete(`${config.apiUrl}/monitors/${monitor.id}`);
      reload();
    };

    // ---------- Réglages ----------
    const saveProxy = async () => {
      savingProxy.value = true;
      proxyMessage.value = "";
      try {
        const { data } = await axios.put(
          `${config.apiUrl}/monitors/transport`,
          { proxy_url: proxyUrl.value },
        );
        proxyMasked.value = data?.proxy_url || "";
        proxyUrl.value = "";
        proxyError.value = false;
        proxyMessage.value = proxyMasked.value
          ? "Proxy enregistré."
          : "Proxy retiré.";
        await loadConnectors();
      } catch (error) {
        proxyError.value = true;
        proxyMessage.value =
          error.response?.data?.error || "Le proxy n’a pas pu être enregistré.";
      } finally {
        savingProxy.value = false;
      }
    };

    onMounted(async () => {
      active = true;
      startAutoRefresh();
      await loadConnectors();
      await Promise.all([reload(), loadTransport()]);
      if (route.query.monitor) dropQueryParams("monitor");
    });

    return {
      coteState,
      refresh,
      refreshing,
      showResultsFor,
      describing,
      paramDescriptions,
      removeParamValue,
      advancedCount,
      clearAdvanced,
      importUrl,
      connectorName,
      connectorsList,
      criteriaSummary,
      currentConnector,
      editor,
      followHit,
      zoomed,
      zoomSource,
      zoomAction,
      formatDate,
      formatPrice,
      frequencies: FREQUENCIES,
      frequencyLabel,
      hitFilter,
      hitImage,
      unknownMonitor,
      hits,
      ignoreAll,
      ignoreHit,
      restoreHit,
      undo,
      undoIgnore,
      loadingHits,
      monitors,
      newCount,
      onConnectorChange,
      openEditor,
      closeEditor,
      drawerDown,
      drawerClick,
      proxyError,
      proxyMasked,
      proxyMessage,
      proxyUrl,
      relative,
      removeMonitor,
      runNow,
      saveMonitor,
      saveProxy,
      savingProxy,
      showHitsFor,
      tab,
      toggleActive,
      transportLabel,
      transports,
    };
  },
};
</script>

<style scoped>
/* Same frame, heading, panel, tabs and toolbar as "Achats remportés" */
.watch-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading,
.watch-panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading > div > span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action {
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #168a64;
  color: #fff;
  font-size: 13px;
  font-weight: 750;
  white-space: nowrap;
  cursor: pointer;
}
.watch-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.tabs {
  display: flex;
  gap: 24px;
  height: 53px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
}
.tabs button {
  position: relative;
  padding: 0;
  border: 0;
  background: none;
  color: #778294;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}
.tabs button.active {
  color: var(--text);
}
.tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--green);
  content: "";
}
.tabs b {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #c84642;
  color: #fff;
  font-size: 11px;
}
.tabs small {
  margin-left: 4px;
  color: #8994a3;
}
.notice {
  margin: 0;
  padding: 10px 18px;
  border-bottom: 1px solid var(--line);
  background: #fff5e8;
  color: #7a4a10;
  font-size: 12px;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #fbfcfd;
}
.toolbar select {
  height: 38px;
  padding: 0 34px 0 12px;
  border-color: #d5dce5;
  color: #4e5a6c;
  font-size: 13px;
}
/* Same refresh button as the overview page */
.heading-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.refresh-button {
  display: flex;
  height: 40px;
  align-items: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #566276;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}
.spinning {
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.reset {
  height: 38px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: #667285;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
select,
input {
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #ccd5df;
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 14px;
}
.primary,
.secondary {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.primary {
  border: 1px solid var(--green);
  background: var(--green);
  color: #fff;
}
.secondary {
  border: 1px solid #cfd7e0;
  background: #fff;
  color: #455165;
}
.small {
  min-height: 32px;
  padding: 0 10px;
  font-size: 12px;
}
.primary:disabled,
.secondary:disabled {
  opacity: 0.5;
}
.link {
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.link--danger,
.error {
  color: #b6423d;
}
.warning {
  color: #9a6400;
}
.empty {
  margin: 0;
  padding: 64px 24px;
  color: var(--muted);
  font-size: 13px;
  text-align: center;
}
.badge {
  display: inline-flex;
  align-self: flex-start;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f1f4f7;
  color: #536073;
  font-size: 11px;
  font-weight: 700;
}
.hits {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  padding: 18px;
}
.hit {
  display: grid;
  grid-template-columns: 96px 1fr;
  grid-template-rows: 1fr auto;
  gap: 10px 12px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.hit--ignored {
  opacity: 0.6;
}
.hit__image {
  display: grid;
  width: 96px;
  height: 96px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #e1e6ec;
  border-radius: 7px;
  background: #f1f4f7;
  color: #a3adba;
}
.hit__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hit__image--zoom {
  padding: 0;
  cursor: zoom-in;
}
.hit-zoom {
  /* Outside the page (teleported): its colours again */
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: calc(56px + var(--safe-top)) 16px calc(16px + var(--safe-bottom));
  background: rgba(8, 14, 24, 0.94);
}
.hit-zoom > img {
  min-height: 0;
  max-width: min(100%, 1100px);
  flex: 0 1 auto;
  object-fit: contain;
  border-radius: 6px;
  cursor: zoom-out;
}
.hit-zoom__close {
  position: absolute;
  top: calc(12px + var(--safe-top));
  right: 16px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}
.hit-zoom__bar {
  display: flex;
  width: min(100%, 1100px);
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff;
}
.hit-zoom__text {
  display: flex;
  min-width: 0;
  flex: 1 1 260px;
  flex-direction: column;
  gap: 2px;
  overflow-wrap: anywhere;
}
.hit-zoom__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.hit__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}
.hit__body strong {
  color: var(--text);
  font-size: 13px;
  line-height: 1.35;
}
.original {
  color: #8994a3;
  font-size: 11px;
}
.drop {
  color: #b6423d;
  font-size: 12px;
  font-weight: 700;
}
.price {
  color: var(--text);
  font-size: 13px;
  font-weight: 800;
}
.meta {
  color: var(--muted);
  font-size: 12px;
}
.hit__actions {
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / -1;
  align-items: center;
  gap: 10px;
}
.hit__actions .link,
.hit__links {
  margin-left: auto;
}
.cote-state {
  font-style: italic;
}
.meta-link {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-decoration: underline dotted;
  cursor: pointer;
}
.meta-link:hover {
  color: #2e6098;
}
.status--link {
  text-decoration: none;
}
.status--link:hover {
  text-decoration: underline;
}
.undo-button {
  height: 30px;
  padding: 0 10px;
  border: 1px solid #d5dce5;
  border-radius: 7px;
  background: #fff;
  color: #566276;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.undo-button:disabled {
  opacity: 0.5;
}
.undo-toast {
  position: fixed;
  z-index: 50;
  bottom: calc(20px + var(--safe-bottom));
  left: 50%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 14px 11px 16px;
  border-radius: 10px;
  background: #101827;
  box-shadow: 0 12px 30px rgba(16, 24, 39, 0.25);
  color: #dbe4f0;
  font-size: 13px;
  transform: translateX(-50%);
}
.undo-toast button {
  padding: 0;
  border: 0;
  background: none;
  color: #8df0c8;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.follow-button,
.ghost-button {
  height: 34px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.follow-button {
  border: 1px solid var(--green);
  background: var(--green);
  color: #fff;
}
.ghost-button {
  border: 1px solid #d8dee6;
  background: #fff;
  color: #596679;
}
.follow-button:disabled,
.ghost-button:disabled {
  opacity: 0.55;
  cursor: wait;
}
.status {
  color: var(--green);
  font-size: 13px;
  font-weight: 700;
}
.monitors {
  padding: 18px;
}
.monitor {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.monitor:last-child {
  margin-bottom: 0;
}
.monitor.paused {
  opacity: 0.65;
}
.monitor__main {
  display: flex;
  min-width: 260px;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}
.monitor__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.monitor__main small {
  color: var(--muted);
  font-size: 12px;
}
.count {
  padding: 2px 8px;
  border-radius: 10px;
  background: #fdecec;
  color: #b6423d;
  font-size: 11px;
  cursor: pointer;
}
.monitor__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}
.switch {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}
.switch input {
  min-height: auto;
}
.settings {
  max-width: 680px;
  padding: 22px;
}
.settings h2 {
  margin: 0 0 6px;
  font-size: var(--section-title-size);
}
.hint {
  margin: 6px 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0;
  color: #536073;
  font-size: 12px;
  font-weight: 700;
}
.buttons {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}
.drawer-layer {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(15, 23, 36, 0.38);
}
.drawer {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(460px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -20px 0 50px rgba(15, 23, 36, 0.14);
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.drawer header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e9ef;
}
.drawer h2 {
  margin: 0;
  font-size: 18px;
}
.close {
  width: 36px;
  height: 36px;
  border: 1px solid #d9e0e8;
  border-radius: 8px;
  background: #fff;
  font-size: 22px;
  cursor: pointer;
}
.drawer form {
  flex: 1;
  overflow: auto;
  padding: 8px 20px 20px;
}
.drawer footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
.import {
  margin-bottom: 8px;
  padding: 12px;
  border: 1px dashed #b9d4ee;
  border-radius: 10px;
  background: #f5f9fd;
}
.import .field {
  margin-top: 0;
}
.advanced {
  padding: 10px 12px;
  border-radius: 8px;
  background: #edf4fb;
  color: #24486e;
  font-size: 12px;
}
.advanced p {
  margin: 0 0 8px;
}
.param {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.param__label {
  min-width: 80px;
  font-weight: 700;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px 3px 9px;
  border: 1px solid #b9d4ee;
  border-radius: 14px;
  background: #fff;
  font-size: 12px;
}
.chip button {
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
@media (max-width: 800px) {
  .watch-page {
    padding: 20px 14px;
  }
  .tabs {
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }
  .tabs::-webkit-scrollbar {
    display: none;
  }
  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .hits,
  .monitors {
    padding: 12px;
  }
}
@media (max-width: 640px) {
  /* Links on their own line, side by side, under Suivre / Ignorer */
  .hit__links {
    flex-basis: 100%;
    margin-left: 0;
  }
  .page-heading {
    flex-direction: column;
    align-items: stretch;
  }
  .heading-actions .primary-action {
    flex: 1;
  }
  .hits {
    grid-template-columns: 1fr;
  }
  .hit {
    grid-template-columns: 80px 1fr;
  }
  .hit__image {
    width: 80px;
    height: 80px;
  }
  select,
  input {
    min-height: 44px;
    font-size: 16px;
  }
}
@media (max-width: 460px) {
  /* Phone: refresh button as an icon next to the title, as on the overview */
  .page-heading {
    position: relative;
  }
  .page-heading > div:first-child {
    padding-right: 52px;
  }
  .refresh-button {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    padding: 0;
    justify-content: center;
  }
  .refresh-label {
    display: none;
  }
}
</style>
